import * as amplitude from '@amplitude/unified';
import { capturePosthog, setPosthogPersonProperties } from './posthog';

const AMPLITUDE_API_KEY = import.meta.env.VITE_AMPLITUDE_API_KEY as string;

let initialized = false;

export const initAmplitude = () => {
  if (import.meta.env.VITE_ENABLE_AMPLITUDE !== 'true' || !AMPLITUDE_API_KEY) return;

  amplitude.initAll(AMPLITUDE_API_KEY, {
    serverZone: 'EU',
    analytics: {
      autocapture: {
        attribution: true,
        fileDownloads: true,
        formInteractions: true,
        pageViews: true,
        sessions: true,
        elementInteractions: true,
        networkTracking: true,
        webVitals: true,
        frustrationInteractions: {
          thrashedCursor: true,
          errorClicks: true,
          deadClicks: true,
          rageClicks: true,
        },
      },
    },
    sessionReplay: {
      sampleRate: 1,
    },
  });

  initialized = true;
};

const noopResult = () => ({ promise: Promise.resolve({ event: { event_type: '' }, code: 0, message: '' }) });

// Dual-send: every tracker funnels through these wrappers, so mirroring here
// covers all custom events / user properties without touching the trackers.
// PostHog forwarding is independent of Amplitude's `initialized` flag so it
// keeps working even if Amplitude is disabled.

// PostHog mirroring is best-effort telemetry — a failure in the PostHog SDK (or
// in the Identify-internals read below) must never break the primary Amplitude
// path, so every mirror call is wrapped and any error swallowed silently.
const mirrorToPosthog = (mirror: () => void): void => {
  try {
    mirror();
  } catch {
    // Intentionally ignored — see comment above.
  }
};

export const track: typeof amplitude.track = (...args) => {
  const [eventInput, eventProperties] = args;
  if (typeof eventInput === 'string') {
    mirrorToPosthog(() =>
      capturePosthog(eventInput, eventProperties as Record<string, unknown> | undefined),
    );
  }
  return initialized ? amplitude.track(...args) : noopResult();
};

export const identify: typeof amplitude.identify = (...args) => {
  mirrorToPosthog(() => {
    const idObj = args[0] as unknown as { getUserProperties?: () => Record<string, unknown> } | undefined;
    if (idObj && typeof idObj.getUserProperties === 'function') {
      // Amplitude's Identify accumulates ops keyed by type; '$set' holds the
      // plain property map we mirror to PostHog person properties.
      const setProps = idObj.getUserProperties()?.['$set'] as Record<string, unknown> | undefined;
      if (setProps && typeof setProps === 'object') setPosthogPersonProperties(setProps);
    }
  });
  return initialized ? amplitude.identify(...args) : noopResult();
};

export const { Identify } = amplitude;
